import clsx from 'clsx';
import type { ReactNode, Ref } from 'react';
import { FiChevronRight } from 'react-icons/fi';

import s from './Layout.module.scss';

/**
 * Reusable layout building blocks: a padded column (`Page`) of glass `Group`s
 * made of `Row`s, plus the `DetailHeader`, chips and buttons that decorate them.
 * Styling lives in the co-located `Layout.module.scss`.
 */

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(s.page, className)}>{children}</div>;
}

export function DetailHeader({
  leading,
  icon,
  title,
  sub,
  trailing,
}: {
  /** Rendered verbatim on the left (e.g. an Avatar). Takes precedence over `icon`. */
  leading?: ReactNode;
  /** Wrapped in the standard rounded icon tile. */
  icon?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <header className={s.detailHeader}>
      {leading ?? (icon != null && <div className={s.detailIcon}>{icon}</div>)}
      <div className={s.detailHeaderText}>
        <h2 className={s.detailTitle}>{title}</h2>
        {sub != null && <p className={s.detailSub}>{sub}</p>}
      </div>
      {trailing}
    </header>
  );
}

export function GroupLabel({ children }: { children: ReactNode }) {
  return <p className={s.groupLabel}>{children}</p>;
}

export function Group({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(s.group, className)}>{children}</div>;
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={s.empty}>{children}</div>;
}

export function CountChip({ children }: { children: ReactNode }) {
  return <span className={s.countChip}>{children}</span>;
}

type RowProps = {
  /** Rendered verbatim on the left (e.g. an Avatar). Takes precedence over `icon`. */
  leading?: ReactNode;
  /** Wrapped in the standard rounded icon tile. */
  icon?: ReactNode;
  title?: ReactNode;
  sub?: ReactNode;
  /** Extra content inside the text column, e.g. a meter. */
  children?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  /** Accessible name for the row's own click target; falls back to a string `title`. */
  label?: string;
  disabled?: boolean;
  className?: string;
  /** The row's root element, e.g. for a drop target. */
  ref?: Ref<HTMLDivElement>;
};

export function Row({
  leading,
  icon,
  title,
  sub,
  children,
  trailing,
  chevron,
  onClick,
  label,
  disabled,
  className,
  ref,
}: RowProps) {
  const interactive = !!onClick;

  return (
    <div ref={ref} className={clsx(s.row, interactive && !disabled && s.rowClickable, className)}>
      {/*
        A stretched, empty button rather than a button around the whole row: `trailing`
        may itself be a control, and nesting buttons is invalid. It comes first so the
        positioned `trailing` paints on top of it and keeps its own clicks.
      */}
      {interactive && (
        <button
          type="button"
          className={s.rowHit}
          onClick={onClick}
          disabled={disabled}
          aria-label={label ?? (typeof title === 'string' ? title : undefined)}
        />
      )}
      {leading ?? (icon != null && <div className={s.rowIcon}>{icon}</div>)}
      <div className={s.rowText}>
        {title != null && <div className={s.rowTitle}>{title}</div>}
        {sub != null && <div className={s.rowSub}>{sub}</div>}
        {children}
      </div>
      {trailing != null && <div className={s.rowTrailing}>{trailing}</div>}
      {chevron && <FiChevronRight className={s.navChevron} />}
    </div>
  );
}

export function BackButton({
  onClick,
  href,
  children,
}: {
  onClick?: () => void;
  href?: string;
  children: ReactNode;
}) {
  if (href) {
    return (
      <a className={s.backBtn} href={href}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={s.backBtn} onClick={onClick}>
      {children}
    </button>
  );
}

export function AddButton({
  icon,
  onClick,
  disabled,
  children,
}: {
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={clsx(s.addBtn, disabled && s.addBtnDisabled)}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {children}
    </button>
  );
}
